import React, { useMemo } from 'react';
import { ScrollView, Text, TouchableOpacity, View } from 'react-native';
import type { AuditLogEntry } from '@oolio-group/audit-sdk';
import Icon from '../../Icon/Icon';
import { useTheme } from '../../../hooks/useTheme';
import { ThemeColors } from '../../../common/theme-colors';
import { makeStyles } from './ActionLogs.styles';

interface Props {
  currentPage: number;
  data: AuditLogEntry[];
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  onPressRow: (log: AuditLogEntry) => void;
}

const LEVEL_ICON_MAP: Record<string, string> = {
  error: 'exclamation-octagon',
  warn: 'exclamation-triangle',
  info: 'info-circle',
  debug: 'info-circle',
};

const getLevelColor = (level: string, colors: ThemeColors) => {
  switch (level) {
    case 'error':
      return colors.negative;
    case 'warn':
      return colors.focus;
    default:
      return colors.neutral;
  }
};

const formatTime = (ts: string) => {
  if (!ts) return '';
  const date = new Date(ts);
  if (Number.isNaN(date.getTime())) return ts;
  return date.toLocaleString('en-US', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });
};

const ActionLogsTable: React.FC<Props> = ({
  currentPage,
  data,
  itemsPerPage,
  onPageChange,
  onPressRow,
}) => {
  const { colors } = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);

  const pageItems = useMemo(
    () =>
      data.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage),
    [data, currentPage, itemsPerPage],
  );

  const pageCount = Math.max(1, Math.ceil(data.length / itemsPerPage));
  const rangeStart = data.length ? (currentPage - 1) * itemsPerPage + 1 : 0;
  const rangeEnd = Math.min(currentPage * itemsPerPage, data.length);

  return (
    <View style={styles.table}>
      <View style={styles.tableHeader}>
        <Text style={[styles.headerText, styles.colTime]}>Time</Text>
        <Text style={[styles.headerText, styles.colType]}>Type</Text>
        <Text style={[styles.headerText, styles.colDescription]}>
          Description
        </Text>
      </View>
      <ScrollView style={styles.tableBody}>
        {pageItems.length ? (
          pageItems.map((log, index) => {
            const level = log.level?.toLowerCase() || 'info';
            return (
              <TouchableOpacity
                testID="row-log"
                key={`${log.ts}-${index}`}
                style={[styles.tableRow, level === 'error' && styles.rowError]}
                onPress={() => onPressRow(log)}
              >
                <View style={styles.levelIcon}>
                  <Icon
                    name={LEVEL_ICON_MAP[level] || 'info-circle'}
                    color={getLevelColor(level, colors)}
                    size={18}
                  />
                </View>
                <Text style={[styles.rowText, styles.colTime]}>
                  {formatTime(log.ts)}
                </Text>
                <Text style={[styles.rowText, styles.colType]}>{log.tag}</Text>
                <Text
                  style={[styles.rowText, styles.colDescription]}
                  numberOfLines={1}
                >
                  {log.msg}
                </Text>
                <Icon name="angle-right" size={20} color={colors.disclosure} />
              </TouchableOpacity>
            );
          })
        ) : (
          <Text style={styles.emptyText}>No logs found</Text>
        )}
      </ScrollView>
      <View style={styles.pagination}>
        <Text style={styles.paginationText}>
          {rangeStart}-{rangeEnd} of {data.length}
        </Text>
        <View style={styles.paginationButtons}>
          <TouchableOpacity
            testID="btn-prevPage"
            disabled={currentPage <= 1}
            style={[styles.pageButton, currentPage <= 1 && styles.disabled]}
            onPress={() => onPageChange(currentPage - 1)}
          >
            <Icon name="angle-left" size={20} color={colors.text} />
          </TouchableOpacity>
          <TouchableOpacity
            testID="btn-nextPage"
            disabled={currentPage >= pageCount}
            style={[
              styles.pageButton,
              currentPage >= pageCount && styles.disabled,
            ]}
            onPress={() => onPageChange(currentPage + 1)}
          >
            <Icon name="angle-right" size={20} color={colors.text} />
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
};

export default ActionLogsTable;
